'use client';

import { Box, type BoxProps } from '@ironhack/design-system2/components/box';
import { chakra } from '@ironhack/design-system2/components/chakra';
import { type ReactElement, useEffect, useState } from 'react';

import { type DatoVideo, type MuxVideo } from '@/lib/datocms/types';

export type VideoPlayerData<
  TVideoKey extends keyof MuxVideo = keyof Omit<MuxVideo, 'duration'>
> = {
  playMode: 'autoPlay' | 'controls';
  video: DatoVideo<TVideoKey>;
} & Pick<DatoVideo, '_updatedAt'>;

type Props = {
  data: VideoPlayerData<'mp4Url' | 'thumbnailUrl'>;
  fallback: ReactElement;
} & BoxProps;

export const VideoPlayer = (props: Props): ReactElement => {
  const {
    data: {
      playMode,
      video: {
        video: { mp4Url, thumbnailUrl },
      },
    },
    fallback,
    ...boxProps
  } = props;

  const thumbUrlObj = new URL(thumbnailUrl);
  thumbUrlObj.searchParams.set('time', '0');

  // waiting -> loading -> ready
  const [loadingState, setLoadingState] = useState<
    'loading' | 'ready' | 'waiting'
  >('waiting');

  useEffect(() => {
    if (loadingState !== 'waiting') return;
    if (document.readyState === 'complete') {
      setLoadingState('loading');
      return;
    }

    const onLoad = (): void => setLoadingState('loading');
    window.addEventListener('load', onLoad);
    return (): void => window.removeEventListener('load', onLoad);
  }, [loadingState]);

  return (
    <Box position="relative" {...boxProps}>
      {fallback}
      {loadingState !== 'waiting' && (
        <chakra.video
          loop
          onLoadStart={() => setLoadingState('ready')}
          position="absolute"
          poster={thumbUrlObj.href}
          src={mp4Url}
          top={0}
          width="full"
          {...(loadingState === 'loading' && { display: 'none' })}
          {...(playMode === 'controls'
            ? { controls: true, cursor: 'pointer' }
            : { autoPlay: true, muted: true, playsInline: true })}
        />
      )}
    </Box>
  );
};
